import { useCallback, useRef, useEffect } from 'react';
import {
  parseReminderLine,
  formatRecurrenceLabel,
  rewriteRelativeLogDates,
  formatLogDate,
  isLogMarkedLine,
} from '../lib/reminderParser';
import { hashReminderLine, computeAclForCreate } from './useReminders';
import { isRemindersEnabled, isDesignatedLogList } from './useUserPrefs';
import { reconcileReminders } from '../lib/reconcileReminders';
import { resolveReminderDate, parseDefaultTime } from '../lib/resolveReminder';
import { parseExistingBellLine } from '../lib/parseExistingBellLine';
import { startAnchorFor } from '../lib/startAnchor';
import {
  PREFIX,
  BELL_RE,
  DONE_BELL_RE,
  COMMENT_PATTERN,
  computeReminderLine,
  bellLineReceipt,
  bellLineCaptureResult,
  detectReminderOnLine,
  inReminderParagraph,
  isReminderLine,
} from '../lib/reminderDetect';

// Done prefix — Walter cycled through three shapes; the current one
// is "[DONE] task". Loud, keyboard-typeable (you can mark a reminder
// done by editing its prefix in place), and the detection regex
// catches it the same way it catches the legacy glyphs:
//   2026-05-11  "~~🔔 task~~"  — markdown strikethrough.
//   2026-05-12  "🔕 task"      — bell-with-slash glyph.
//   2026-05-13  "[DONE] task"  — explicit text marker (current).
// Legacy forms still match via DONE_BELL_RE for backwards compat
// and migrate to [DONE] via stripRecurrenceGlyph on read.
const DONE_PREFIX = '[DONE] ';

// Detection patterns + the 🔔-rewrite / line-detection helpers live in
// ../lib/reminderDetect (PREFIX, BELL_RE, DONE_BELL_RE,
// CONTINUATION_PATTERN, COMMENT_PATTERN, GENERAL_LIST_KEYWORD_RE,
// computeReminderLine, detectReminderOnLine) — imported above so this
// hook and the CodeMirror-6 editor share one copy and can't drift.

/**
 * useReminderCapture — owns reminder behaviour for a single textarea
 * (one ListPanel / FocusedPanel instance). Three flows share this hook:
 *
 *   1. KEYWORD CAPTURE (non-designated lists)
 *      A line starting with `reminder:` / `todo:` + Enter auto-converts:
 *      (a) resolve "tomorrow"/"Sun" etc. into an absolute M/D,
 *      (b) prepend 🔔, (c) write a Firestore reminder doc, (d) push an
 *      undo toast. No confirm step — the keyword is the opt-in.
 *
 *   2. DESIGNATED LIST AUTO-PARSE (chunk 4)
 *      Every Enter-completed line in the designated reminder list is
 *      treated as auto-confirmed — same date-resolve + 🔔 prefix as
 *      capture, no banner. (Multi-line paragraph parsing — first line
 *      = phrase, rest = notes — comes in a follow-up; this is the
 *      single-line shape Walter actually types in.)
 *
 *   3. BELL-REMOVAL DELETE
 *      When the user edits a 🔔-prefixed line and removes the 🔔, the
 *      paired reminder doc is deleted. Detected by diffing prev/next
 *      content on each save. Also works if the user deletes the entire
 *      line.
 *
 * Args:
 *   list, userPrefs, createReminder — same as before.
 *   reminders        — array from useReminders, used for bell-removal
 *                      reminder lookup by sourceLineHash.
 *   deleteReminder   — from useReminders.
 *   pushToast        — from BoardPage, (msg, undoAction) => void.
 *   onContentMutate  — ({from, to, insert}) => void. Replaces the
 *                      [from, to) range of the textarea content with
 *                      `insert`. (Was insertAt/insertText; switched to
 *                      a range-replace API so the confirm flow can swap
 *                      "tomorrow" for "5/3" in addition to prepending
 *                      🔔.)
 *
 * Returns:
 *   handleKeyDown    — wrap textarea's onKeyDown.
 *   onContentChanged — call on each save with (prev, next) to handle
 *                      bell-removal deletes.
 *   isDesignatedList, isLogList — designation flags for the caller.
 */
export function useReminderCapture({
  list,
  // boardId is needed so created reminders can be opened from the
  // agenda regardless of which board the user is currently on. Old
  // reminders without sourceBoardId fall back to current-board lookup
  // — i.e. their "open in source" only works if you're already on
  // the right board. Once they're rewritten / re-confirmed, they
  // pick up sourceBoardId.
  boardId,
  // currentUid — for Bug #12 / shared reminder sync. Used to build the
  // `aclUids` denormalised array on each createReminder call (the source
  // list's editors + viewers + owner), so a collaborator's reminders on
  // a shared list surface in this user's agenda via the collectionGroup
  // query in useReminders. Optional for now — fallback is `[currentUid]`
  // (own-only visibility, identical to pre-#12 behaviour).
  currentUid,
  userPrefs,
  createReminder,
  updateReminder,
  reminders,
  deleteReminder,
  pushToast,
  onContentMutate,
}) {
  // Keep the latest reminders array reachable from inside callbacks
  // without making every callback depend on the array (it changes on
  // every Firestore snapshot).
  const remindersRef = useRef(reminders);
  useEffect(() => { remindersRef.current = reminders; }, [reminders]);
  // Walter 2026-09-14: his default reminder time, readable from inside the
  // reconciler's callback without joining its dependency array.
  const defaultTimeRef = useRef(parseDefaultTime(userPrefs?.defaultReminderTime));
  defaultTimeRef.current = parseDefaultTime(userPrefs?.defaultReminderTime);

  // Same trick for `list` (so computeAclForCreate(...) sees fresh
  // editors/viewers when the parent's list snapshot updates) and
  // `currentUid` (Bug #12). The createReminder callback's deps array
  // intentionally only watches `list?.id` — re-creating that callback
  // on every Firestore snapshot of the list doc would churn the
  // textarea wiring. Refs give us freshness without that churn.
  const listRef = useRef(list);
  useEffect(() => { listRef.current = list; }, [list]);
  const currentUidRef = useRef(currentUid);
  useEffect(() => { currentUidRef.current = currentUid; }, [currentUid]);

  // Walter 2026-05-16: in-flight create dedup. Two paths can ask for
  // a reminder to be created against the same bell-line hash within
  // microseconds:
  //   1. The Enter-time auto-capture (`scanLineOnEnter` →
  //      `commitReminder`) writes the doc AND mutates the textarea
  //      content to prepend 🔔.
  //   2. The mutation re-fires the textarea's onChange, which calls
  //      `onContentChanged` — sees a "new" bell line, and the
  //      cached `remindersList` (a Firestore snapshot) doesn't yet
  //      have the doc from path 1 (still in flight). So path 2
  //      creates a SECOND doc with the same hash.
  // This ref tracks hashes for which a create is in flight; both
  // paths consult it as a synchronous gate, so the dedup works even
  // when the snapshot hasn't caught up. Cleaned up after the create
  // resolves; if cleanup loses the race with the next call, the
  // snapshot-based check in `remindersList.find(...)` catches it.
  const pendingCreateHashes = useRef(new Set());

  // Are we operating in designated-reminder-list mode? When true, we
  // skip the banner and auto-confirm every Enter-completed line.
  // Plural-aware: a list is designated if its ID is in the user's
  // `designatedReminderListIds` array. Falls back to the legacy
  // singular field for older accounts that haven't migrated yet.
  const isDesignatedList = !!(
    list?.id && userPrefs && (
      (Array.isArray(userPrefs.designatedReminderListIds)
        && userPrefs.designatedReminderListIds.includes(list.id))
      || userPrefs.designatedReminderListId === list.id
    )
  );
  // Log-list designation — distinct from the reminder designation.
  // Lines auto-prepend `@today` on Enter, and `@today`/`@yesterday`
  // tokens get rewritten to literal `@YYYY-MM-DD` so the source text
  // is self-describing forever after.
  const isLogList = isDesignatedLogList(userPrefs, list?.id);

  /**
   * Resolve the user-configured default time for date-only reminders.
   * Stored as "HH:MM"; falls back to 7am if missing or malformed.
   */
  function getDefaultTimeFromPrefs() {
    // Walter 2026-09-14: one parser for the setting, shared with the
    // popover (lib/resolveReminder). Same rules as before.
    return parseDefaultTime(userPrefs?.defaultReminderTime);
  }

  /**
   * computeNewLineForConfirm — thin wrapper over the pure
   * computeReminderLine (../lib/reminderDetect), binding the userPrefs-
   * derived default time. The 🔔-rewrite spec, colon-insertion, and
   * leading-punctuation strip all live in that shared module now so the
   * CodeMirror-6 editor can reuse them byte-identically.
   */
  function computeNewLineForConfirm(line, parse) {
    return computeReminderLine(line, parse, getDefaultTimeFromPrefs());
  }

  /**
   * effectiveTriggerDate — the Date we store in Firestore for when the
   * reminder should fire. If chrono was certain about the hour, use
   * it as-is. Otherwise apply the user-configured default time
   * (Settings → "default reminder time", falls back to 7am).
   */
  function effectiveTriggerDate(parse) {
    if (parse.kind !== 'dated' || !parse.parsedDate) return null;
    // Walter 2026-09-14: the shared rule (lib/resolveReminder). Identical
    // result here; the point is that the popover now answers the same way.
    return resolveReminderDate({
      parsedDate: parse.parsedDate,
      hasCertainTime: !!parse.hasCertainTime,
      defaultTime: getDefaultTimeFromPrefs(),
    });
  }

  /**
   * commitReminder — shared by banner-confirm and designated auto-flow.
   * Writes the Firestore doc, mutates the textarea content from raw
   * line → resolved+🔔 line, and (for capture flow) emits an undo toast.
   */
  // applyMutation (default true) — the textarea path lets commitReminder rewrite
  // the source line via onContentMutate. The CM6 editor sets it false: it owns
  // the document and applies the 🔔-rewrite itself as one atomic transaction, so
  // commitReminder only creates the doc + toast (no text side effect, no race).
  const commitReminder = useCallback(async ({ line, lineStart, parse, source, effectiveLine, applyMutation = true }) => {
    // Walter 2026-06-09 (#3 / #70): when chrono invented a year the user
    // didn't type AND the date lands far out (>~6 months), confirm it —
    // a stale "5/30" silently becoming next May was surprising. Within
    // ~6 months (the normal Dec→Jan roll) we don't ask. OK keeps the
    // invented (next) year; Cancel uses the current year (overdue).
    if (parse && parse.kind === 'dated' && parse.yearImputed && parse.parsedDate
        && typeof window !== 'undefined' && typeof window.confirm === 'function') {
      const d = new Date(parse.parsedDate);
      const SIX_MONTHS_MS = 183 * 86400000;
      if (d.getTime() - Date.now() > SIX_MONTHS_MS) {
        const fmt = (dt) => dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
        const thisYear = new Date(d);
        thisYear.setFullYear(new Date().getFullYear());
        const keep = window.confirm(
          `You didn't type a year, and that date has already passed this year — `
          + `so this reminder is set for ${fmt(d)}.\n\n`
          + `OK = keep ${fmt(d)}.\n`
          + `Cancel = use ${fmt(thisYear)} instead (this year, overdue).`
        );
        if (!keep) {
          parse = { ...parse, parsedDate: thisYear, yearImputed: false };
        }
      }
    }
    // effectiveLine — set by the general-list keyword path in
    // scanLineOnEnter: the keyword-stripped remainder. The 🔔 line is
    // composed from it (so the resolved bell-line doesn't carry the
    // redundant "reminder:" keyword), while the REPLACED textarea
    // range still spans the full original `line` (keyword included),
    // so the keyword is consumed. Defaults to `line` for the
    // designated-list + banner paths that don't strip anything.
    const composeLine = (effectiveLine != null) ? effectiveLine : line;
    const newLine = computeNewLineForConfirm(composeLine, parse);
    // F3 fix (Walter 2026-05-24): strip the leading bell with BELL_RE
    // — the canonical anchored regex used everywhere else that reads
    // bell-lines (collectBellLineEntries, the AgendaPanel detector,
    // BoardPage's source-line scan). The previous `.replace(PREFIX,'')`
    // used a plain string match, which (a) didn't handle the
    // recurring-reminder `🔔↻ ` form so the hash kept the leading
    // bell, and (b) had no anchor, so any future caller passing a
    // newLine that didn't start with PREFIX would have a mid-line
    // 🔔 stripped instead. Same regex as collectBellLineEntries
    // means the de-dup lookup in onContentChanged can actually find
    // the in-flight doc and skip the duplicate-create path cleanly.
    const finalHash = hashReminderLine(newLine.replace(BELL_RE, ''));
    // Skip if another path is already creating (or just created) this
    // exact bell-line hash. See pendingCreateHashes comment above.
    const existingInSnapshot = (remindersRef.current || []).find(
      r => r.sourceListId === list.id && r.sourceLineHash === finalHash
    );
    if (existingInSnapshot || pendingCreateHashes.current.has(finalHash)) {
      // Still need to mutate the line into its resolved form — that's
      // the visible "✓ I got your reminder" feedback. Skip only the
      // create.
      if (applyMutation && typeof onContentMutate === 'function') {
        onContentMutate({
          from: lineStart,
          to: lineStart + line.length,
          insert: newLine,
        });
      }
      return;
    }
    pendingCreateHashes.current.add(finalHash);
    let newId = null;
    try {
      newId = await createReminder({
        sourceListId: list.id,
        sourceBoardId: boardId || null,
        sourceLineHash: finalHash,
        text: parse.kind === 'dated'
          ? parse.restText
          : (parse.restText || line.trim()),
        parsedDate: effectiveTriggerDate(parse),
        recurrence: parse.recurrence || null,
        startDate: startAnchorFor(parse),   // #246
        // Bug #12: snapshot the source list's ACL so collaborators
        // see this reminder via the collectionGroup query. See
        // useReminders / shared-reminder-sync proposal for details.
        // Read via refs so we capture the freshest editors/viewers
        // without re-running the callback on every list snapshot.
        aclUids: computeAclForCreate(listRef.current, currentUidRef.current),
      });
    } catch (err) {
      console.error('createReminder failed:', err);
    } finally {
      // Hold the in-flight marker briefly past the await so the
      // snapshot-listener has a moment to deliver the new doc into
      // remindersRef. Without this delay the onContentChanged-path
      // race window reopens immediately after the await resolves.
      setTimeout(() => pendingCreateHashes.current.delete(finalHash), 2500);
    }

    if (applyMutation && typeof onContentMutate === 'function') {
      onContentMutate({
        from: lineStart,
        to: lineStart + line.length,
        insert: newLine,
      });
    }

    // Confirm toast — fires on EVERY successful create, capture or
    // designated. Walter's ask: users want to see at a glance that
    // their phrasing was understood, with the parsed date/time and
    // a snippet of what they typed. The capture flow additionally
    // wires up an undo action; the designated flow doesn't (every
    // Enter would otherwise spawn an undo, which is noisy — the
    // bell-removal scan is the natural undo for designated lists).
    if (pushToast && newId) {
      const text = (parse.kind === 'dated' ? parse.restText : parse.restText) || line.trim();
      const snippet = text.length > 38 ? text.slice(0, 36).trim() + '…' : text;
      let when = '';
      if (parse.kind === 'recurring' && parse.recurrence) {
        // Recurring: "every weekday at 7am" — defer to the parser's
        // own label fn for consistency with the inline tag.
        when = formatRecurrenceLabel(parse.recurrence) || '';
      } else {
        const d = effectiveTriggerDate(parse);
        if (d instanceof Date && !isNaN(d.getTime())) {
          when = d.toLocaleString(undefined, {
            weekday: 'short', month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit',
          });
        }
      }
      const message = when
        ? `🔔 reminder set: "${snippet}" — ${when}`
        : `🔔 reminder set: "${snippet}"`;

      const undo = (source === 'capture' && deleteReminder)
        ? async () => {
            try { await deleteReminder(newId); } catch (e) { console.warn('[reminder undo] delete failed', e); }
            if (applyMutation && typeof onContentMutate === 'function') {
              onContentMutate({
                from: lineStart,
                to: lineStart + newLine.length,
                insert: line, // restore the user's original
              });
            }
          }
        : null;

      pushToast(message, undo, { ttlMs: source === 'capture' ? 5000 : 4000 });
    }

    return newId;
  }, [createReminder, list?.id, onContentMutate, deleteReminder, pushToast]);

  /**
   * handleEnterForLog — runs on Enter BEFORE scanLineOnEnter and the
   * browser's default newline. Two behaviours, both rewriting the
   * line the user just committed (NOT the new line — Walter wants
   * dates to read as "this entry happened on date X" prefixed to the
   * line, not stamped as a header above an unrelated next line):
   *
   *   (1) Literalize relative @-dates. If the just-completed line
   *       contains `@today` / `@yesterday` / `@tomorrow`, rewrite the
   *       tokens to `@YYYY-MM-DD` so the line is self-describing in
   *       source forever after. Runs in any list, designated or not.
   *
   *   (2) Auto-prepend in a designated log list. If the just-
   *       completed line has content AND doesn't already start with
   *       `@<date>`, prepend `@<today> ` so the line reads as a
   *       complete dated entry. Skipped for continuation lines
   *       (whitespace / bullet glyphs at start) so notes attached to
   *       a previous entry don't accidentally become their own
   *       dated rows.
   *
   * Returns `true` if it handled the Enter (preventDefault applied);
   * caller should NOT fall through to scanLineOnEnter or the default
   * newline.
   */
  const handleEnterForLog = useCallback((e, text, cursor) => {
    const before = text.slice(0, cursor);
    const after = text.slice(cursor);
    const lineStart = before.lastIndexOf('\n') + 1;
    const lineText = before.slice(lineStart);
    const trimmedLine = lineText.trim();
    if (trimmedLine.length === 0) return false; // blank line, default Enter

    // Mid-line Enter (cursor not at end of line — there's still
    // content to the right on this line). Skip both rewrite and
    // prepend so the user's split lands where they expect.
    const midLine = after.length > 0 && !after.startsWith('\n');
    if (midLine) return false;

    // Continuation lines (whitespace / bullets) — never auto-stamped.
    // Reuses the same glyph set as the reminder continuation rule.
    const isContinuation = /^[\s\-*·.+>]/.test(lineText);

    const lineHasAtDate = lineText.includes('@');
    const startsWithAtDate = /^\s*@\S/.test(lineText);

    // Nothing to do if there's no work to do in either path.
    if (!lineHasAtDate && !(isLogList && !isContinuation)) return false;

    const today = new Date();

    // Path A: line has an @-date somewhere. Literalize relative
    // tokens. Don't double-prepend even in a log list.
    if (lineHasAtDate) {
      const rewritten = rewriteRelativeLogDates(lineText, today);
      if (rewritten === lineText) return false; // nothing to rewrite, default Enter
      e.preventDefault();
      e.stopPropagation();
      onContentMutate?.({
        from: lineStart,
        to: cursor,
        insert: rewritten + '\n',
      });
      return true;
    }

    // Path B: log-list prepend. Line has no @-date and isn't a
    // continuation; we drop the date stamp in front of it on commit.
    if (isLogList && !isContinuation && !startsWithAtDate) {
      e.preventDefault();
      e.stopPropagation();
      const stamp = `@${formatLogDate(today)} `;
      onContentMutate?.({
        from: lineStart,
        to: cursor,
        insert: stamp + lineText + '\n',
      });
      return true;
    }
    return false;
  }, [isLogList, onContentMutate]);

  const scanLineOnEnter = useCallback((text, cursor) => {
    if (!isRemindersEnabled(userPrefs)) return;
    // Log lists don't auto-confirm or keyword-capture reminders. The
    // user picked "log mode" — lines stay in source as @date entries.
    // Bell-prefixed lines (the user's explicit reminder opt-in) still
    // become reminders via the onContentChanged diff scanner, just
    // not via this Enter-time auto-creation path.
    if (isLogList) return;

    const before = text.slice(0, cursor);
    const lineStart = before.lastIndexOf('\n') + 1;
    const lineText = before.slice(lineStart);

    // Walter 2026-09-14 (reminder proposal, step 3): the same receipt as the
    // live-preview editor's Enter - a line typed into after a leftover 🔔
    // gets the canonical form, so its date is tinted (SyntaxBackdrop draws
    // that tint on this path too). Text only: no document is created; the
    // reconciler matches the edited line back to its reminder. Must run
    // BEFORE the bell guards below, which would otherwise skip it.
    const receipt = bellLineReceipt(lineText, getDefaultTimeFromPrefs());
    if (receipt && typeof onContentMutate === 'function') {
      onContentMutate({ from: lineStart, to: lineStart + lineText.length, insert: receipt.newLine });
      return;
    }

    // Already 🔔-marked (with or without space after the bell) — skip.
    // Walter 2026-05-11: also catch leading-whitespace variants (e.g.
    // " 🔔 something" if a stray space crept in). The previous strict
    // anchor sometimes missed lines with weird leading characters and
    // double-bells got produced when the designated-list fallback
    // path re-prefixed a bell-line as an "undated reminder".
    if (BELL_RE.test(lineText) || DONE_BELL_RE.test(lineText)) return;
    if (/^\s*(?:~~)?🔔/.test(lineText)) return;
    // Walter 2026-05-28: also skip lines that contain 🔔 anywhere
    // (mid-line, not just leading). The seeded Reminders list has an
    // instructional line "Click the 🔔 in the top header to view and
    // manage all reminders." — Walter pressed Enter at end of it and
    // the auto-capture wrapped the whole sentence as a reminder,
    // producing "🔔 Click the 🔔 in the top header...". A line that
    // already mentions the bell explicitly is almost certainly
    // explanatory, not a reminder the user wants. Cheap check; the
    // false-positive (user wanting to capture a line with 🔔 in the
    // middle) is vanishingly rare.
    if (lineText.includes('🔔')) return;
    // `@`-marked log lines never trigger designated-list auto-confirm
    // or keyword capture. The user's intent with the @ marker is
    // "log this", not "ping me". Bell-prefixed lines that happen to
    // also contain `@today` hit the BELL_RE branch above first.
    if (isLogMarkedLine(lineText)) return;

    const trimmedLine = lineText.trim();
    if (trimmedLine.length === 0) return;

    // `//`-prefixed lines are commentary in any list, designated or not.
    // Always skip — both prevents the seeded "//Note: ..." example
    // lines from firing on a fresh reminder list AND lets users keep
    // ad-hoc notes in their reminder list without them auto-capturing.
    if (COMMENT_PATTERN.test(lineText)) return;

    // Shared detection (../lib/reminderDetect): in a designated list
    // every non-continuation paragraph is a reminder (undated fallback
    // when it doesn't parse as dated); on a general list only a
    // `reminder:` / `todo:` keyword line captures, with the keyword
    // stripped for the compose text. The Enter-path-specific guards
    // above (mid-line 🔔, @-log lines) already ran; detect re-checks the
    // shared bell/comment/continuation skips harmlessly.
    // Walter 2026-08-25: a line whose paragraph already starts with a reminder
    // is a NOTE on that reminder, not a reminder of its own — the blank line is
    // the separator, and it's one you can see.
    const det = detectReminderOnLine(lineText, {
      isDesignatedList,
      isNote: inReminderParagraph(text.slice(0, lineStart)),
    });
    if (!det) return;
    if (isDesignatedList) {
      // No banner — auto-confirm every designated-list paragraph.
      commitReminder({ line: lineText, lineStart, parse: det.parse, source: 'designated' });
    } else {
      // Walter 2026-05-21: the keyword IS the opt-in, so no confirm
      // banner. source='capture' wires up the undo toast; effectiveLine
      // is the keyword-stripped remainder so the composed 🔔 line drops
      // the redundant "reminder:" keyword while the replaced textarea
      // range still spans the full original line.
      commitReminder({
        line: lineText,
        lineStart,
        parse: det.parse,
        source: 'capture',
        effectiveLine: det.composeLine,
      });
    }
  }, [userPrefs, isDesignatedList, isLogList, commitReminder, onContentMutate]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const target = e.target;
      const cursor = target.selectionStart;
      const text = target.value;
      // Log-line handling (rewrite @today, auto-stamp in log lists)
      // wins over reminder scanning. When it returns true, the Enter
      // is fully handled — the default newline was preventDefault'd
      // and the line was rewritten via onContentMutate.
      if (handleEnterForLog(e, text, cursor)) return true;
      scanLineOnEnter(text, cursor);
    }
    return false;
  }, [scanLineOnEnter, handleEnterForLog]);

  /**
   * onContentChanged — call from the textarea's onChange with prev/next
   * content snapshots. Detects 🔔-prefixed lines whose bells were
   * removed (or whose lines were deleted altogether) and deletes the
   * matching Firestore reminder.
   *
   * Heuristic: only act when the TOTAL count of 🔔-prefixed lines
   * decreased. If the count stayed the same, the user is rephrasing
   * an existing bell-line — keep the reminder, don't delete just
   * because the line's hash drifted. This is the rule Walter asked
   * for: "bell removed → delete; bell still present → keep, even if
   * text changes".
   *
   * For each line that lost its bell (or was outright deleted), we
   * hash the remembered post-prefix text and look up a reminder by
   * sourceLineHash. The hash is computed off the resolved (date-
   * normalized) text we wrote on confirm — so a clean removal will
   * always match a doc.
   */
  const reconcileContent = useCallback(async (prevContent, nextContent) => {
    if (!list?.id) return;
    if (prevContent === nextContent) return;
    const prevEntries = collectBellLineEntries(prevContent);
    const nextEntries = collectBellLineEntries(nextContent);
    const remindersList = remindersRef.current || [];

    // Walter 2026-05-13: prefix flips on stable lines. When the user
    // edits "🔔 task" → "[DONE] task" (or vice versa), the hash is
    // unchanged so the three-way diff below sees nothing. Catch the
    // flip here and update the reminder's status accordingly.
    if (updateReminder) {
      const prevByHash = new Map(prevEntries.map(e => [e.hash, e]));
      for (const next of nextEntries) {
        const prev = prevByHash.get(next.hash);
        if (!prev || prev.prefix === next.prefix) continue;
        const isDoneNow = next.prefix === DONE_PREFIX;
        const r = remindersList.find(
          x => x.sourceListId === list.id && x.sourceLineHash === next.hash
        );
        if (!r) continue;
        const currentStatus = r.status || 'open';
        const nextStatus = isDoneNow ? 'done' : 'open';
        if (currentStatus === nextStatus) continue;
        try {
          await updateReminder(r.id, { status: nextStatus });
        } catch (e) {
          console.warn('[reminder status flip] update failed', e);
        }
      }
    }

    // Walter 2026-06-16: FULL RECONCILIATION (lib/reconcileReminders) replaces
    // the old fragile hash-diff + 1:1 rephrase pairing. The old approach only
    // updated a doc when EXACTLY one line was removed and one added, so
    // near-duplicate lines, multi-line edits, or a capture firing mid-typing
    // left docs with stale text ("call tom a" vs "call tom again"), orphans,
    // or duplicates. Now we match the CURRENT bell lines to this list's docs
    // by hash, then by content similarity — so an in-place edit UPDATES its
    // doc (preserving id → notes/snooze, fixing the text), unrelated delete+add
    // stay separate, and there's exactly one doc per line. Pure + unit-tested
    // (lib/reconcileReminders.test.js).
    const myDocs = remindersList.filter(r => r.sourceListId === list.id);
    const lineInputs = nextEntries.map((e) => {
      // Walter 2026-09-26: these are EXISTING bell lines. Parsed with the
      // default rules, a line ending in "?" came back "not a reminder" and
      // fieldsFor then wrote parsedDate: null - editing such a line DELETED
      // its date while the line still displayed it. Read them the way
      // capture read them. See lib/parseExistingBellLine.
      const parse = parseExistingBellLine(e.content);
      const text = parse.kind === 'dated'
        ? (parse.restText || '')
        : (parse.restText || e.content).trim();
      return { entry: e, parse, hash: e.hash, text };
    });
    const docInputs = myDocs.map(r => ({
      id: r.id, sourceLineHash: r.sourceLineHash, text: r.text || '',
    }));
    const { matches, newLineIndexes, staleDocIds } = reconcileReminders(docInputs, lineInputs);

    // Shared field builder for a parsed line (used by update + create).
    // `doc` is the reminder this line already belongs to, when there is one.
    //
    // Walter 2026-08-24: when the edited line names a date but NO time, keep
    // the reminder's EXISTING time-of-day instead of dropping to the 7am
    // default. Editing "9/1 9am: test one" down to "9/6: test one" silently
    // moved it to 7am — the 2026-05-18 lesson ("snooze means later, same
    // time, never a surprise 7am") applies just as much to a date edit. The
    // default still applies where there's nothing to preserve: a new line.
    // Walter 2026-09-14: the shared rule (lib/resolveReminder) — a typed time
    // wins, else keep the reminder's existing time, else his default. The
    // one behaviour change: a NEW line uses his configured default time
    // instead of a hard-coded 7am.
    const fieldsFor = (parse, entry, doc) => {
      if (parse.kind === 'dated') {
        return {
          text: parse.restText,
          parsedDate: resolveReminderDate({
            parsedDate: parse.parsedDate,
            hasCertainTime: !!parse.hasCertainTime,
            defaultTime: defaultTimeRef.current,
            existingDate: doc ? doc.parsedDate : null,
          }),
          recurrence: parse.recurrence || null,
          // Walter 2026-09-26 (#246): remember a start date he TYPED, so the
          // agenda's writeback can keep "starting 10/2" in the line. null when
          // there is none, so removing the date from the line clears it too.
          startDate: startAnchorFor(parse),
        };
      }
      return {
        text: (parse.restText || entry.content).trim(),
        parsedDate: null,
        recurrence: null,
        startDate: null,
      };
    };

    // UPDATE: similarity-matched lines (the line was edited). Exact-hash
    // matches are unchanged lines — the status-flip loop above already
    // handled any 🔔↔[DONE] toggle on them, so we don't touch them here...
    //
    // ...with ONE exception (Walter 2026-08-24): a doc with NO date whose
    // line clearly HAS one. That combination is unreachable by design —
    // "remove date" in the agenda rewrites the line to drop the date too —
    // so it only exists where the doc and its line already drifted apart,
    // which is Walter's "shows in the view but with no date chip". Healing
    // it needs no guess: the line says what the date is. Deliberately
    // one-directional — a doc that HAS a date is left alone, because a
    // snooze legitimately moves the doc's date ahead of any rewrite.
    if (updateReminder) {
      for (const m of matches) {
        if (m.reason === 'hash') {
          const { entry, parse } = lineInputs[m.lineIndex];
          if (parse.kind !== 'dated' || !parse.parsedDate) continue;
          const doc = myDocs.find(r => r.id === m.docId);
          if (!doc || doc.parsedDate || doc.recurrence) continue;
          try {
            await updateReminder(m.docId, fieldsFor(parse, entry, doc));
          } catch (e) { console.warn('[reminder heal] update failed', e); }
          continue;
        }
        if (m.reason !== 'similar') continue;
        const { entry, parse } = lineInputs[m.lineIndex];
        const patch = {
          ...fieldsFor(parse, entry, myDocs.find(r => r.id === m.docId)),
          sourceLineHash: entry.hash,
          status: entry.prefix === DONE_PREFIX ? 'done' : 'open',
        };
        try { await updateReminder(m.docId, patch); } catch (e) { console.warn('[reminder reparse] update failed', e); }
      }
    }

    // DELETE: docs whose source line is gone.
    if (deleteReminder) {
      for (const id of staleDocIds) {
        try { await deleteReminder(id); } catch (e) { console.warn('[reminder remove] delete failed', e); }
      }
    }

    // CREATE: brand-new lines. Dedup against the cached snapshot AND any
    // in-flight creates from the Enter-time commitReminder path (without
    // this, a complete bell line + Enter would write two docs for one hash).
    // Walter 2026-05-13: a line typed as "[DONE] task" seeds a 'done' doc.
    if (createReminder) {
      for (const i of newLineIndexes) {
        const { entry, parse } = lineInputs[i];
        if (myDocs.some(r => r.sourceLineHash === entry.hash)) continue;
        if (pendingCreateHashes.current.has(entry.hash)) continue;
        pendingCreateHashes.current.add(entry.hash);
        const f = fieldsFor(parse, entry);
        const payload = {
          sourceListId: list.id,
          sourceBoardId: boardId || null,
          sourceLineHash: entry.hash,
          text: f.text,
          parsedDate: f.parsedDate,
          recurrence: f.recurrence,
          status: entry.prefix === DONE_PREFIX ? 'done' : 'open',
          // Bug #12 — denormalised ACL; see commitReminder above.
          aclUids: computeAclForCreate(listRef.current, currentUidRef.current),
        };
        try {
          await createReminder(payload);
        } catch (err) {
          console.warn('[reminder add] create failed', err);
        } finally {
          setTimeout(() => pendingCreateHashes.current.delete(entry.hash), 2500);
        }
      }
    }
  }, [list?.id, deleteReminder, updateReminder, createReminder, boardId]);

  // ── DEBOUNCE + SERIALISE (Walter 2026-08-24) ────────────────────────────
  // reconcileContent used to run on EVERY keystroke, firing Firestore writes
  // per character with no ordering guarantee. Editing a reminder's date in
  // place therefore raced itself: each half-typed state ("🔔 8/2 11am: …",
  // "🔔 8/2" …) wrote its own parse into the doc, and whichever write happened
  // to land LAST won. That's the reminder-drift Walter reported on 2026-08-20
  // — a line reading one date while its chip showed another, or no chip at
  // all, or the reminder sorting as if it had moved to a year away. The line
  // text was never the problem; the doc had simply kept a mid-typing value.
  //
  // So: coalesce changes and run once the typing stops, keeping the FIRST
  // prev of the batch (the bell-removal and status-flip logic diff against
  // it), and never run two passes at once — if content changed while a pass
  // was in flight, run again after it with the newest text. A pleasant side
  // effect for a cost-sensitive app: one reconcile per edit burst instead of
  // one per keystroke, and far fewer reminder writes.
  const ccTimer = useRef(null);
  const ccPrev = useRef(null);
  const ccNext = useRef(null);
  const ccRunning = useRef(false);

  const runReconcile = useCallback(async () => {
    if (ccTimer.current) { clearTimeout(ccTimer.current); ccTimer.current = null; }
    if (ccPrev.current == null) return;
    if (ccRunning.current) return;         // the tail below picks it up
    ccRunning.current = true;
    try {
      // Loop rather than recurse: keystrokes that arrive DURING a pass are
      // reconciled by the next iteration, against the newest text.
      while (ccPrev.current != null) {
        const prev = ccPrev.current;
        const next = ccNext.current;
        ccPrev.current = null;
        ccNext.current = null;
        try { await reconcileContent(prev, next); } catch (e) { console.warn('[reminder reconcile] failed', e); }
      }
    } finally {
      ccRunning.current = false;
    }
  }, [reconcileContent]);

  const onContentChanged = useCallback((prevContent, nextContent) => {
    if (!list?.id) return;
    if (ccPrev.current == null) ccPrev.current = prevContent;
    ccNext.current = nextContent;
    if (ccTimer.current) clearTimeout(ccTimer.current);
    ccTimer.current = setTimeout(() => { runReconcile(); }, 900);
  }, [list?.id, runReconcile]);

  /**
   * flushReminderReconcile() — run any pending reconcile NOW. Called on blur
   * and before organize, so leaving a list (or sorting it) never strands an
   * edit that the debounce hadn't got to yet.
   */
  const flushReminderReconcile = useCallback(async () => {
    if (ccPrev.current == null) return;
    await runReconcile();
  }, [runReconcile]);

  // Don't strand a pending reconcile when the panel unmounts.
  useEffect(() => () => {
    if (ccTimer.current) { clearTimeout(ccTimer.current); ccTimer.current = null; }
    if (ccPrev.current != null) runReconcile();
  }, [runReconcile]);

  // Walter 2026-06-09: capture lines that were typed but never "committed"
  // with Enter. Without this, a line you typed and then walked away from
  // (or hit organize on) never became a reminder — it got dumped under
  // "doesn't look like a reminder" or lost on leave. Runs on blur and is
  // also exposed for the organize path. Scans EVERY uncaptured line in one
  // pass, rewrites them all in a single content mutation, batch-creates the
  // docs, and shows ONE summary undo toast.
  const captureAllUncaptured = useCallback(async (text) => {
    if (!list?.id || isLogList) return;
    // Walter 2026-06-09: never auto-capture an UNTOUCHED seeded demo list
    // — its example lines aren't the user's real reminders. Once they
    // edit it (seededFromTemplate clears), capture activates normally.
    if (listRef.current?.seededFromTemplate) return;
    const content = typeof text === 'string' ? text : '';
    if (!content.trim()) return;
    const lines = content.split('\n');
    const newLines = lines.slice();
    const toCreate = []; // { parse, finalHash }
    // Walter 2026-08-25: track whether the paragraph we're in already holds a
    // reminder — everything after one, until the next blank line, is a note on
    // it. Set by lines we CONVERT too, so "reminder / detail / detail" typed in
    // one go captures the first and keeps the rest as its notes.
    let paragraphHasReminder = false;
    for (let i = 0; i < lines.length; i++) {
      const lineText = lines[i];
      const trimmed = lineText.trim();
      if (!trimmed) { paragraphHasReminder = false; continue; }
      const isNote = paragraphHasReminder;
      if (isReminderLine(lineText)) paragraphHasReminder = true;
      if (/^\s*#/.test(lineText)) continue;                                // section header (blur-path only)
      // Shared detection (../lib/reminderDetect) also skips bell /
      // comment / continuation lines and requires the keyword on a
      // general list — so this path and the Enter-time scan can't drift.
      // The `#`-header skip above stays inline: it's blur-path-only (the
      // Enter path intentionally never skipped headers).
      const det = detectReminderOnLine(lineText, { isDesignatedList, isNote });
      if (!det) continue;
      const { parse, composeLine } = det;
      const newLine = computeNewLineForConfirm(composeLine, parse);
      paragraphHasReminder = true;   // this line is about to become one
      const finalHash = hashReminderLine(newLine.replace(BELL_RE, ''));
      if ((remindersRef.current || []).some(r => r.sourceListId === list.id && r.sourceLineHash === finalHash)) continue;
      if (pendingCreateHashes.current.has(finalHash)) continue;
      pendingCreateHashes.current.add(finalHash);
      newLines[i] = newLine;
      toCreate.push({ parse, finalHash });
    }
    if (toCreate.length === 0) return;
    const nextContent = newLines.join('\n');
    if (typeof onContentMutate === 'function') {
      onContentMutate({ from: 0, to: content.length, insert: nextContent });
    }
    const createdIds = [];
    for (const { parse, finalHash } of toCreate) {
      let parsedDate = null;
      if (parse.kind === 'dated' && parse.parsedDate) {
        if (parse.hasCertainTime) {
          parsedDate = parse.parsedDate;
        } else {
          const d = new Date(parse.parsedDate);
          const { hour, minute } = getDefaultTimeFromPrefs();
          d.setHours(hour, minute, 0, 0);
          parsedDate = d;
        }
      }
      try {
        const id = await createReminder({
          sourceListId: list.id,
          sourceBoardId: boardId || null,
          sourceLineHash: finalHash,
          text: parse.kind === 'dated' ? parse.restText : (parse.restText || ''),
          parsedDate,
          recurrence: parse.recurrence || null,
          startDate: startAnchorFor(parse),   // #246
          aclUids: computeAclForCreate(listRef.current, currentUidRef.current),
        });
        if (id) createdIds.push(id);
      } catch (err) {
        console.warn('[reminder blur-capture] create failed', err);
      } finally {
        setTimeout(() => pendingCreateHashes.current.delete(finalHash), 2500);
      }
    }
    if (pushToast) {
      const n = toCreate.length;
      pushToast(`${n} reminder${n > 1 ? 's' : ''} set`, async () => {
        if (typeof onContentMutate === 'function') {
          onContentMutate({ from: 0, to: nextContent.length, insert: content });
        }
        for (const id of createdIds) { try { await deleteReminder?.(id); } catch {} }
      });
    }
  }, [list?.id, boardId, isDesignatedList, isLogList, userPrefs, createReminder, deleteReminder, pushToast, onContentMutate]);

  const handleBlur = useCallback((e) => {
    const text = e?.target?.value;
    if (typeof text === 'string') captureAllUncaptured(text);
  }, [captureAllUncaptured]);

  // CM6-native capture (Walter 2026-07-15). Given ONE line's text, decide if it
  // should become a reminder and return `{ newLine, commit }` — or null. The CM6
  // editor applies `newLine` itself as one atomic transaction (so the rewrite
  // can't race live typing), then calls commit() to create the doc with
  // applyMutation:false (no text side effect here). Mirrors scanLineOnEnter's
  // Enter-path guards; reuses the shared detect + rewrite so it can't drift from
  // the textarea path.
  const captureLineForCM6 = useCallback((lineText, textBefore, opts) => {
    if (!list?.id) return null;
    // `textBefore` is REQUIRED — it's how we tell a note from a reminder. A
    // call site that forgets it silently turns every note into a reminder,
    // which is exactly what happened when the CM6 leave-a-line trigger was
    // missed (Walter 2026-08-25). Undefined is a wiring bug, not a value.
    if (textBefore === undefined && typeof console !== 'undefined') {
      console.warn('[reminder capture] captureLineForCM6 called without textBefore — notes will be misread as reminders');
    }
    const raw = lineText || '';
    const trimmed = raw.trim();

    // ── Log @date stamping (mirrors handleEnterForLog; runs BEFORE reminder
    //    capture, like the Enter path). Returns just { newLine } — no doc. The
    //    CM6 editor applies it atomically. Continuation lines (leading ws /
    //    bullets) are never auto-stamped in a log list. ──
    if (trimmed.length > 0) {
      const today = new Date();
      if (raw.includes('@')) {
        // Literalize relative @-dates (@today → @YYYY-MM-DD) in ANY list.
        const rewritten = rewriteRelativeLogDates(raw, today);
        if (rewritten !== raw) return { newLine: rewritten };
      } else if (isLogList && !/^[\s\-*·.+>]/.test(raw) && !/^\s*@\S/.test(raw)) {
        // Designated log list: prepend today's stamp to a dated entry.
        return { newLine: `@${formatLogDate(today)} ${raw}` };
      }
    }

    // ── Reminder capture ──
    // Walter 2026-09-14 (reminder proposal, step 3): a line typed into after
    // a leftover 🔔 has a correct reminder but no receipt. Give it the
    // canonical form on Enter, and on leaving the line - but on leaving ONLY
    // if he edited it during the visit (his "i": track edits). The editor
    // passes opts.wasEdited; the leave-a-line trigger applies a result only
    // when it has a commit, and bellLineCaptureResult adds one only then, so
    // clicking through old bell lines still never rewrites them.
    const receipt = bellLineCaptureResult(raw, getDefaultTimeFromPrefs(), {
      leavingEditedLine: !!(opts && opts.wasEdited),
    });
    if (receipt) return receipt;
    if (raw.includes('🔔')) return null;   // mid-line bell → leave alone
    if (isLogMarkedLine(raw)) return null;  // @-log line → not a reminder
    if (trimmed.length === 0) return null;
    if (COMMENT_PATTERN.test(raw)) return null;
    // Walter 2026-08-25: the paragraph decides. `textBefore` is everything
    // above this line in the document — the CM6 editor passes it because,
    // unlike the textarea paths, this one only ever saw a single line.
    const det = detectReminderOnLine(raw, {
      isDesignatedList,
      isNote: inReminderParagraph(textBefore || ''),
    });
    if (!det) return null;
    const newLine = computeNewLineForConfirm(det.composeLine, det.parse);
    const source = isDesignatedList ? 'designated' : 'capture';
    return {
      newLine,
      commit: () => commitReminder({
        line: raw,
        lineStart: 0, // unused when applyMutation:false
        parse: det.parse,
        source,
        effectiveLine: det.composeLine,
        applyMutation: false,
      }),
    };
  }, [list?.id, isDesignatedList, isLogList, commitReminder]);

  return {
    handleKeyDown,
    handleBlur,
    captureAllUncaptured,
    captureLineForCM6,
    onContentChanged,
    flushReminderReconcile,
    isDesignatedList,
    isLogList,
  };
}

/**
 * collectBellLineEntries — for each 🔔-prefixed line, returns
 * `{hash, content}` where hash is the FNV-1a of the post-prefix text
 * and content is that text itself. Order-preserving (line order
 * within the source content). Used by the rephrase-reparse logic
 * to pair prev-missing with next-new entries by index.
 */
function collectBellLineEntries(content) {
  const out = [];
  if (!content) return out;
  const lines = content.split('\n');
  for (const line of lines) {
    // Match either prefix form, with OR without trailing whitespace.
    // The regex captures so we know how many chars to slice off.
    const doneM = line.match(DONE_BELL_RE);
    if (doneM) {
      const post = line.slice(doneM[0].length);
      out.push({ hash: hashReminderLine(post), content: post, prefix: DONE_PREFIX });
      continue;
    }
    const bellM = line.match(BELL_RE);
    if (bellM) {
      const post = line.slice(bellM[0].length);
      // Walter 2026-06-24: skip a bare "🔔 " with no body. The Format-menu
      // reminder button inserts an empty bell for the user to fill in — we
      // must NOT create a blank reminder until they actually type something
      // (otherwise a mis-click litters the agenda). The reminder materializes
      // on the first real character, via the next onContentChanged diff.
      if (post.trim() === '') continue;
      out.push({ hash: hashReminderLine(post), content: post, prefix: PREFIX });
    }
  }
  return out;
}
